import ConfirmDialog from '@/components/ConfirmDialog';
import ContactsFilterModal from '@/components/ContactsFilterModal';
import ExportContactsModal from '@/components/ExportContactsModal';
import ImportContactsModal from '@/components/ImportContactsModal';
import ProjectContactForm from '@/components/ProjectContactForm';
import SortableHeader from '@/components/SortableHeader';
import TablePagination from '@/components/TablePagination';
import { useToast } from '@/hooks/useToast';
import apiService from '@/services/api';
import { projectContactsService } from '@/services/projectContactsService';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Project Contacts page component with full CRUD functionality
 * @returns {JSX.Element} ProjectContacts component
 */
const ProjectContacts = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();
  const { showSuccess, showError } = useToast();

  // State management
  const [contacts, setContacts] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    tags: [],
    from: '',
    to: '',
  });
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Sort and pagination states
  const [sortOptions, setSortOptions] = useState({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Modal states
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    hasPrevPage: false,
    hasNextPage: false,
  });

  // Bulk selection state
  const [rowSelection, setRowSelection] = useState({});
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Load contacts from API
  const loadContacts = useCallback(
    async (
      pageIndex = 0,
      search = globalFilter,
      appliedFilters = filters,
      appliedSortOptions = sortOptions
    ) => {
      try {
        setIsLoading(true);

        // Build filter parameters
        const filterParams = {
          page: pageIndex + 1, // API expects 1-based page numbers
          limit: pagination.pageSize,
          search,
          sortBy: appliedSortOptions.sortBy,
          sortOrder: appliedSortOptions.sortOrder,
        };

        // Add individual filters if they have values
        if (appliedFilters.tags && appliedFilters.tags.length > 0) {
          filterParams.tags = appliedFilters.tags
            .map(t => (typeof t === 'string' ? t : t._id))
            .join(',');
        }
        if (appliedFilters.from) filterParams.from = appliedFilters.from;
        if (appliedFilters.to) filterParams.to = appliedFilters.to;

        const response = await projectContactsService.getProjectContacts(
          projectId,
          filterParams
        );

        // API returns { success, docs, totalDocs, limit, page, totalPages, hasPrevPage, hasNextPage, ... }
        setContacts(response.docs || []);
        setPagination(prev => ({
          ...prev,
          pageIndex,
          total: response.totalDocs || 0,
          totalPages: response.totalPages || 0,
          hasPrevPage: response.hasPrevPage || false,
          hasNextPage: response.hasNextPage || false,
        }));

        // Clear row selection when data changes
        setRowSelection({});
        setSelectedContacts([]);
      } catch (error) {
        console.error('Error loading contacts:', error);
        // Keep existing contacts on error
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, globalFilter, pagination.pageSize, filters, sortOptions]
  );

  // Consolidate loading logic into a single effect
  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (!projectId) return;

    if (isInitialMount.current) {
      loadContacts();
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      loadContacts(0, globalFilter);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [projectId, globalFilter, loadContacts]);

  // Modal handlers
  const handleApplyFilters = newFilters => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  // Handle column header click for sorting
  const handleColumnSort = columnId => {
    // Map column IDs to API field names
    const columnToFieldMap = {
      name: 'name',
      phoneNumber: 'phoneNumber',
      dateOfBirth: 'dateOfBirth',
      createdAt: 'createdAt',
    };

    const fieldName = columnToFieldMap[columnId];
    if (!fieldName) return; // Skip if column doesn't support sorting

    // Determine new sort order
    let newSortOrder = 'asc';
    if (sortOptions.sortBy === fieldName && sortOptions.sortOrder === 'asc') {
      newSortOrder = 'desc';
    }

    // Update sort options
    const newSortOptions = {
      sortBy: fieldName,
      sortOrder: newSortOrder,
    };

    setSortOptions(newSortOptions);
    // Reset to first page when sort changes
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    // Reload contacts with new sort options
    loadContacts(0, globalFilter, filters, newSortOptions);
  };

  const hasActiveFilters =
    filters.tags.length > 0 || filters.from !== '' || filters.to !== '';

  // Bulk selection handlers
  const handleSelectAll = useCallback(() => {
    if (Object.keys(rowSelection).length === contacts.length) {
      setRowSelection({});
      setSelectedContacts([]);
    } else {
      const newSelection = {};
      contacts.forEach((contact, index) => {
        newSelection[index] = true;
      });
      setRowSelection(newSelection);
      setSelectedContacts(contacts);
    }
  }, [contacts, rowSelection]);

  const handleSelectRow = useCallback(
    (rowIndex, contact) => {
      const newSelection = { ...rowSelection };
      if (newSelection[rowIndex]) {
        delete newSelection[rowIndex];
        setSelectedContacts(prev => prev.filter(c => c._id !== contact._id));
      } else {
        newSelection[rowIndex] = true;
        setSelectedContacts(prev => [...prev, contact]);
      }
      setRowSelection(newSelection);
    },
    [rowSelection]
  );

  // Define columns
  // Modal triggers
  const handleEditContact = useCallback(contact => {
    setEditingContact(contact);
    setShowContactForm(true);
  }, []);

  const handleDeleteContact = useCallback(contact => {
    setContactToDelete(contact);
    setShowDeleteDialog(true);
  }, []);

  const handleStartChat = useCallback(
    async contact => {
      try {
        const contactId = contact._id || contact.id;
        if (!contactId) {
          showError('Invalid contact data');
          return;
        }

        const response = await apiService.initiateConversation(
          contactId,
          projectId
        );

        if (response.success) {
          const conversationId = response.data?._id || response.data?.id;
          if (conversationId) {
            navigate(`/projects/${projectId}/conversations/${conversationId}`);
          } else {
            // Fallback if ID is missing but success is true
            navigate(`/projects/${projectId}/conversations`);
          }
        } else {
          showError(response.message || 'Failed to start conversation');
        }
      } catch (error) {
        console.error('Error starting chat:', error);
        showError('Failed to start conversation. Please try again.');
      }
    },
    [navigate, projectId, showError]
  );

  const columns = useMemo(
    () => [
      // Selection column
      columnHelper.display({
        id: 'select',
        header: () => (
          <div className="flex items-center justify-start">
            <input
              type="checkbox"
              checked={
                Object.keys(rowSelection).length === contacts.length &&
                contacts.length > 0
              }
              onChange={handleSelectAll}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
        ),
        size: 50,
        cell: ({ row }) => (
          <div className="flex items-center justify-start">
            <input
              type="checkbox"
              checked={rowSelection[row.index] || false}
              onChange={() => handleSelectRow(row.index, row.original)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
        ),
        enableSorting: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        size: 200,
        cell: ({ getValue }) => (
          <div className="text-sm font-medium text-gray-900 text-left">
            {getValue()}
          </div>
        ),
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('phoneNumber', {
        header: 'Phone Number',
        size: 200,
        cell: ({ getValue }) => (
          <div className="text-sm text-gray-900 text-left">{getValue()}</div>
        ),
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('tags', {
        header: 'Tags',
        size: 150,
        cell: ({ getValue }) => {
          const tags = getValue() || [];
          return (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, idx) => (
                <span
                  key={tag._id || idx}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                  style={{ backgroundColor: tag.color || '#3B82F6' }}
                >
                  {tag.name}
                </span>
              ))}
              {tags.length === 0 && <span className="text-gray-400">-</span>}
            </div>
          );
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('dateOfBirth', {
        header: 'Date of Birth',
        size: 150,
        cell: ({ getValue }) => (
          <div className="text-sm text-gray-500 text-left">
            {getValue() ? new Date(getValue()).toLocaleDateString() : '-'}
          </div>
        ),
        enableSorting: true,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        size: 150,
        cell: ({ getValue }) => (
          <div className="text-sm text-gray-500 text-left">
            {getValue() ? new Date(getValue()).toLocaleDateString() : '-'}
          </div>
        ),
        enableSorting: true,
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: 'chat',
        header: 'Chat',
        size: 120,
        cell: ({ row }) => (
          <div className="text-left text-sm font-medium">
            <button
              className="text-primary-600 hover:text-primary-900"
              onClick={() => handleStartChat(row.original)}
              title="Send Message"
            >
              Send Message
            </button>
          </div>
        ),
        enableSorting: false,
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 150,
        cell: ({ row }) => {
          const contact = row.original;
          return (
            <div className="flex items-center space-x-3 text-left text-sm font-medium">
              <button
                className="text-primary-600 hover:text-primary-900"
                onClick={() => handleEditContact(contact)}
              >
                Edit
              </button>
              <button
                className="text-red-600 hover:text-red-900"
                onClick={() => handleDeleteContact(contact)}
              >
                Delete
              </button>
            </div>
          );
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [
      columnHelper,
      contacts.length,
      handleSelectAll,
      handleSelectRow,
      rowSelection,
      handleStartChat,
      handleEditContact,
      handleDeleteContact,
    ]
  );

  // Create table instance
  const table = useReactTable({
    data: contacts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination,
      sorting: [
        {
          id: sortOptions.sortBy,
          desc: sortOptions.sortOrder === 'desc',
        },
      ],
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    pageCount: pagination.totalPages,
    columnResizeMode: 'onChange',
  });

  // CRUD Handlers
  const handleCreateContact = async contactData => {
    try {
      setIsCreating(true);
      await projectContactsService.createProjectContact(projectId, contactData);
      showSuccess('Contact created successfully');
      setShowContactForm(false);
      loadContacts(pagination.pageIndex); // Reload current page
    } catch (error) {
      console.error('Error creating contact:', error);
      showError(
        error.response?.data?.message ||
          error.message ||
          'Failed to create contact'
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateContact = async contactData => {
    try {
      setIsUpdating(true);
      await projectContactsService.updateProjectContact(
        projectId,
        editingContact._id,
        contactData
      );
      showSuccess('Contact updated successfully');
      setShowContactForm(false);
      setEditingContact(null);
      loadContacts(pagination.pageIndex); // Reload current page
    } catch (error) {
      console.error('Error updating contact:', error);
      showError(
        error.response?.data?.message ||
          error.message ||
          'Failed to update contact'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmDeleteContact = async () => {
    try {
      setIsDeleting(true);
      await projectContactsService.deleteProjectContact(
        projectId,
        contactToDelete._id
      );
      showSuccess('Contact deleted successfully');
      setShowDeleteDialog(false);
      setContactToDelete(null);
      loadContacts(pagination.pageIndex); // Reload current page
    } catch (error) {
      console.error('Error deleting contact:', error);
      showError(
        error.response?.data?.message ||
          error.message ||
          'Failed to delete contact'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle import success
  const handleImportSuccess = () => {
    loadContacts(0); // Go to first page after import
  };

  // Export XLSX
  const handleExport = async (exportFilters = {}) => {
    try {
      const blob = await projectContactsService.exportProjectContacts(
        projectId,
        {
          format: 'xlsx',
          fields: ['name', 'phoneNumber', 'dateOfBirth', 'createdAt'],
          filters: exportFilters,
        }
      );

      saveAs(
        blob,
        `project-contacts-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      showError('Error exporting contacts. Please try again.');
    }
  };

  return (
    <div className="h-full bg-gray-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white shadow flex-shrink-0">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Project Contacts
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage contacts for this project ({pagination.total} contacts)
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => setShowContactForm(true)}
                className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg
                  className="-ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span className="hidden sm:inline">Add Contact</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span className="hidden sm:inline">Import Contacts</span>
                <span className="sm:hidden">Import</span>
              </button>
              <button
                onClick={() => setShowExportModal(true)}
                className="px-3 py-2 sm:px-4 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span className="hidden sm:inline">Export Contacts</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Global Search */}
            <div>
              <label
                htmlFor="global-search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search
              </label>
              <input
                type="text"
                id="global-search"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search contacts..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            {/* Filter Toggle Button */}
            <div className="flex items-end">
              <button
                onClick={() => setShowFilterModal(true)}
                className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  hasActiveFilters
                    ? 'border-primary-500 text-primary-700 bg-primary-50'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filter
                {hasActiveFilters && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {
                      [
                        filters.tags.length > 0,
                        filters.from !== '',
                        filters.to !== '',
                      ].filter(Boolean).length
                    }
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-gray-500">Loading contacts...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Single Table with Fixed Header and Scrollable Body */}
            <div className="flex-1 overflow-auto">
              <table className="min-w-full">
                <thead className="bg-[#fcfcfd] sticky top-0 z-10 shadow-sm border-b border-gray-200">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => {
                        if (header.column.getCanSort()) {
                          return (
                            <SortableHeader
                              key={header.id}
                              columnId={header.column.id}
                              label={flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              sortBy={sortOptions.sortBy}
                              sortOrder={sortOptions.sortOrder}
                              onSort={handleColumnSort}
                              className="bg-gray-50 bg-opacity-75 backdrop-blur backdrop-filter"
                            />
                          );
                        }
                        return (
                          <th
                            key={header.id}
                            scope="col"
                            className="px-3 sm:px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest bg-gray-50 bg-opacity-75 backdrop-blur backdrop-filter"
                            style={{ width: header.getSize() }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-3 sm:px-6 py-12 text-center text-gray-500"
                      >
                        {isLoading
                          ? 'Loading contacts...'
                          : 'No contacts found.'}{' '}
                        {globalFilter &&
                          !isLoading &&
                          'Try adjusting your search criteria.'}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="px-3 sm:px-6 py-4 whitespace-nowrap"
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {selectedContacts.length > 0 && (
          <div className="flex-shrink-0 bg-yellow-50 px-4 py-2 border-t border-yellow-200 flex items-center justify-between">
            <span className="text-sm font-medium text-yellow-800">
              {selectedContacts.length} contacts selected
            </span>
          </div>
        )}

        {/* Standard Pagination */}
        <TablePagination
          pagination={pagination}
          onPageChange={pageIndex => loadContacts(pageIndex)}
          onPageSizeChange={pageSize => {
            setPagination(prev => ({
              ...prev,
              pageSize,
              pageIndex: 0,
            }));
            loadContacts(0, globalFilter, filters, sortOptions);
          }}
        />
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4 text-center">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowContactForm(false);
                setEditingContact(null);
              }}
            ></div>
            <div className="relative inline-block align-middle bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <ProjectContactForm
                contact={editingContact}
                onSubmit={
                  editingContact ? handleUpdateContact : handleCreateContact
                }
                onCancel={() => {
                  setShowContactForm(false);
                  setEditingContact(null);
                }}
                isLoading={isCreating || isUpdating}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Contact"
        message={`Are you sure you want to delete "${contactToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteContact}
        onCancel={() => {
          setShowDeleteDialog(false);
          setContactToDelete(null);
        }}
        isLoading={isDeleting}
        variant="danger"
      />

      {/* Import Contacts Modal */}
      <ImportContactsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        projectId={projectId}
        onImportSuccess={handleImportSuccess}
      />

      {/* Filter & Sort Modal */}
      <ContactsFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filters={filters}
        onApplyFilters={handleApplyFilters}
      />
      <ExportContactsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        projectId={projectId}
      />
    </div>
  );
};

export default ProjectContacts;
